"use client";

import React, { useState } from "react";
import TabWithTable from "../layout/tabWithTable";
import BottomCurves from "../curves/bottomCurves";
import { getBackgroundColor } from "@/utils/colorHelpers";

type ContentItem = {
    heading: string;
    description: string;
};

type FeatureType = {
    heading: string;
    description: string;
};

type TabItem = {
    id: number;
    tabName: string;
    feature: FeatureType;
    contents: ContentItem[];
};

type TabWithTableContentProps = {
    data: {
        heading: string;
        description?: string;
        tabWithTable: TabItem[];
        waveBgColor: {
            waveBgColor: string;
        };
        backgroundColor: {
            backgroundColor: string;
        };
    };
};

export default function TabWithTableContent({
    data,
}: TabWithTableContentProps) {
    const [activeTab, setActiveTab] = useState(0);
    const background = getBackgroundColor(data.backgroundColor?.backgroundColor);

    return (
        <section className="relative pb-28 md:pb-[10rem]" style={{ background }}>
            <div className="px-8 sm:px-6 md:px-10 lg:px-24">
                {data.heading && (
                    <h2 className="text-gray-900 font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-tight mb-4 text-center uppercase tracking-tight">
                        {data.heading}
                    </h2>
                )}

                {data.description && (
                    <p className="text-gray-600 text-base md:text-lg lg:text-xl text-center mb-10 max-w-3xl mx-auto">
                        {data.description}
                    </p>
                )}

                {/* Mobile dropdown */}
                <div className="block md:hidden mb-8">
                    <select
                        value={activeTab}
                        onChange={(e) => setActiveTab(Number(e.target.value))}
                        className="w-full rounded-xl border-2 border-purple bg-white px-4 py-3 text-purple font-medium focus:outline-none focus:ring-2 focus:ring-purple"
                    >
                        {data.tabWithTable?.map((tab, index) => (
                            <option key={tab.id} value={index}>
                                {tab.tabName}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Desktop tabs */}
                <div className="hidden md:flex flex-wrap justify-center gap-3 md:gap-4 mb-10">
                    {data.tabWithTable?.map((tab, index) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(index)}
                            className={`px-5 py-2.5 md:px-6 md:py-3 rounded-full text-sm md:text-base font-medium transition-all duration-300 border-2 ${
                                activeTab === index
                                    ? "bg-purple text-white border-purple"
                                    : "bg-transparent text-purple border-purple hover:bg-purple/10"
                            }`}
                        >
                            {tab.tabName}
                        </button>
                    ))}
                </div>

                {data.tabWithTable?.[activeTab] && (
                    <TabWithTable
                        feature={data.tabWithTable[activeTab].feature}
                        contents={data.tabWithTable[activeTab].contents}
                    />
                )}
            </div>

            <BottomCurves
                color={data.waveBgColor.waveBgColor}
                direction={"right"}
            />
        </section>
    );
}
